import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const INITIAL_DATA = {
  examType: 'Follicle Count',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  rightOvary: {
    length: '',
    width: '',
    depth: '',
    totalFolliclesGeq10: '',
    totalFolliclesLt10: '',
    folliclesGeq10: [],
    folliclesLt10: []
  },
  leftOvary: {
    length: '',
    width: '',
    depth: '',
    totalFolliclesGeq10: '',
    totalFolliclesLt10: '',
    folliclesGeq10: [],
    folliclesLt10: []
  },
  endometrium: {
    phase: 'Early proliferative',
    thickness: ''
  }
};

function FollicleCountForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Right Ovary, 1=Left Ovary, 2=Endometrium
  const [activeTab, setActiveTab] = useState(0);

  const { technologists, defaultTech } = useTechnologists();

  const {
    formData,
    error,
    success,
    isLoading,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    updateField,
    updateNestedField,
    resetForm,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    { ...INITIAL_DATA, technologist: defaultTech },
    'Follicle Count',
    { token, facilityInfo }
  );

  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist]);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    await hookHandleSubmit(e);
  }, [hookHandleSubmit]);

  // Follicle list helpers
  const addFollicle = (ovary) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    const newFollicle = { dim1: '', dim2: '', dim3: '' };
    updateNestedField(`${ovaryKey}.folliclesGeq10`, [...formData[ovaryKey].folliclesGeq10, newFollicle]);
  };

  const removeFollicle = (ovary, index) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    updateNestedField(
      `${ovaryKey}.folliclesGeq10`,
      formData[ovaryKey].folliclesGeq10.filter((_, i) => i !== index)
    );
  };

  const handleFollicleChange = (ovary, index, field, value) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    updateNestedField(
      `${ovaryKey}.folliclesGeq10`,
      formData[ovaryKey].folliclesGeq10.map((f, i) =>
        i === index ? { ...f, [field]: value } : f
      )
    );
  };

  const addFollicleLt10 = (ovary) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    updateNestedField(`${ovaryKey}.folliclesLt10`, [...formData[ovaryKey].folliclesLt10, { dim1: '', dim2: '', dim3: '' }]);
  };

  const removeFollicleLt10 = (ovary, index) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    updateNestedField(`${ovaryKey}.folliclesLt10`, formData[ovaryKey].folliclesLt10.filter((_, i) => i !== index));
  };

  const handleFollicleLt10Change = (ovary, index, field, value) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    updateNestedField(
      `${ovaryKey}.folliclesLt10`,
      formData[ovaryKey].folliclesLt10.map((f, i) =>
        i === index ? { ...f, [field]: value } : f
      )
    );
  };

  const calcVolume = (length, width, depth) => {
    const l = parseFloat(length);
    const w = parseFloat(width);
    const d = parseFloat(depth);
    if (isNaN(l) || isNaN(w) || isNaN(d)) return null;
    return (l * w * d * 0.52).toFixed(1);
  };

  const renderOvaryTab = (side) => {
    const ovaryKey = side === 'right' ? 'rightOvary' : 'leftOvary';
    const ovaryData = formData[ovaryKey];
    const label = side === 'right' ? 'Right Ovary' : 'Left Ovary';
    const volume = calcVolume(ovaryData.length, ovaryData.width, ovaryData.depth);

    return (
      <div className="tab-content-grid">
        <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>{label}</h3>

        {/* Ovary Size */}
        <div className="size-inputs">
          <label>Size (L x W x D cm):</label>
          <input
            type="number"
            step="0.1"
            min="0"
            placeholder="Length"
            value={ovaryData.length}
            onChange={(e) => updateNestedField(`${ovaryKey}.length`, e.target.value)}
          />
          <span>×</span>
          <input
            type="number"
            step="0.1"
            min="0"
            placeholder="Width"
            value={ovaryData.width}
            onChange={(e) => updateNestedField(`${ovaryKey}.width`, e.target.value)}
          />
          <span>×</span>
          <input
            type="number"
            step="0.1"
            min="0"
            placeholder="Depth"
            value={ovaryData.depth}
            onChange={(e) => updateNestedField(`${ovaryKey}.depth`, e.target.value)}
          />
          {volume !== null && (
            <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>
              = {volume} cc
            </span>
          )}
        </div>

        {/* Follicle counts */}
        <div className="form-group" style={{ marginTop: '20px' }}>
          <label>Total Follicles ≥10mm:</label>
          <input
            type="number"
            min="0"
            max="50"
            step="1"
            placeholder="0"
            value={ovaryData.totalFolliclesGeq10}
            onChange={(e) => updateNestedField(`${ovaryKey}.totalFolliclesGeq10`, e.target.value)}
            style={{ width: '100px' }}
          />
        </div>

        <div className="form-group">
          <label>Total Follicles &lt;10mm:</label>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            placeholder="0"
            value={ovaryData.totalFolliclesLt10}
            onChange={(e) => updateNestedField(`${ovaryKey}.totalFolliclesLt10`, e.target.value)}
            style={{ width: '100px' }}
          />
        </div>

        {/* Individual follicle measurements ≥10mm */}
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ color: 'var(--color-primary)', marginBottom: '10px' }}>
            Individual Follicle Measurements ≥10mm
          </h4>

          {ovaryData.folliclesGeq10.map((follicle, index) => (
            <div key={index} className="mass-section" style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'bold', minWidth: '80px' }}>
                  Follicle {index + 1}:
                </span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="dim1"
                  value={follicle.dim1}
                  onChange={(e) => handleFollicleChange(side, index, 'dim1', e.target.value)}
                  style={{ width: '80px' }}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="dim2"
                  value={follicle.dim2}
                  onChange={(e) => handleFollicleChange(side, index, 'dim2', e.target.value)}
                  style={{ width: '80px' }}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="dim3"
                  value={follicle.dim3}
                  onChange={(e) => handleFollicleChange(side, index, 'dim3', e.target.value)}
                  style={{ width: '80px' }}
                />
                <span style={{ color: 'var(--color-text-muted)', fontSize: '0.9em' }}>mm</span>
                <button
                  type="button"
                  onClick={() => removeFollicle(side, index)}
                  className="remove-btn"
                  style={{ marginLeft: '8px' }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => addFollicle(side)}
            className="add-mass-btn"
          >
            ＋ Add Follicle ≥10mm
          </button>
        </div>

        {/* Individual follicle measurements <10mm */}
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ color: 'var(--color-primary)', marginBottom: '10px' }}>
            Individual Follicle Measurements &lt;10mm
          </h4>

          {ovaryData.folliclesLt10.map((follicle, index) => (
            <div key={index} className="mass-section" style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'bold', minWidth: '80px' }}>
                  Follicle {index + 1}:
                </span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="dim1"
                  value={follicle.dim1}
                  onChange={(e) => handleFollicleLt10Change(side, index, 'dim1', e.target.value)}
                  style={{ width: '80px' }}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="dim2"
                  value={follicle.dim2}
                  onChange={(e) => handleFollicleLt10Change(side, index, 'dim2', e.target.value)}
                  style={{ width: '80px' }}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="dim3"
                  value={follicle.dim3}
                  onChange={(e) => handleFollicleLt10Change(side, index, 'dim3', e.target.value)}
                  style={{ width: '80px' }}
                />
                <span style={{ color: 'var(--color-text-muted)', fontSize: '0.9em' }}>mm</span>
                <button
                  type="button"
                  onClick={() => removeFollicleLt10(side, index)}
                  className="remove-btn"
                  style={{ marginLeft: '8px' }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => addFollicleLt10(side)}
            className="add-mass-btn"
          >
            ＋ Add Follicle &lt;10mm
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo
          formData={formData}
          updateField={updateField}
          technologists={technologists}
          fieldErrors={fieldErrors}
        />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
          <div className="form-group">
            <label>Reason for Exam:</label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => updateField('reason', e.target.value)}
              placeholder="Enter reason for exam"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            {['Right Ovary', 'Left Ovary', 'Endometrium'].map((tabLabel, tabIndex) => (
              <button
                key={tabLabel}
                type="button"
                onClick={() => setActiveTab(tabIndex)}
                className={`worksheet-tab-btn${activeTab === tabIndex ? ' active' : ''}`}
              >
                {tabLabel}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Right Ovary Tab */}
            {activeTab === 0 && renderOvaryTab('right')}

            {/* Left Ovary Tab */}
            {activeTab === 1 && renderOvaryTab('left')}

            {/* Endometrium Tab */}
            {activeTab === 2 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Endometrium</h3>

                <div className="form-group">
                  <label>Phase:</label>
                  <select
                    value={formData.endometrium.phase}
                    onChange={(e) => updateNestedField('endometrium.phase', e.target.value)}
                  >
                    <option value="Early proliferative">Early proliferative</option>
                    <option value="Late proliferative">Late proliferative</option>
                    <option value="Secretory">Secretory</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Thickness (mm):</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="Measurement"
                    value={formData.endometrium.thickness}
                    onChange={(e) => updateNestedField('endometrium.thickness', e.target.value)}
                    style={{ width: '120px' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default FollicleCountForm;
